import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Copy,
  Folder,
  FolderOpen,
  Globe,
  ImagePlus,
  Loader2,
  Search,
  Upload,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  buildPickerListRows,
  buildPickerReferencePayload,
  buildPickerVaultForest,
  filterPickerItems,
  PICKER_VAULT_LABEL,
  pickerItemsQuery,
  pickerListContext,
  pickerUploadFolderId,
  type PickerListRow,
  type PickerNav,
  type PickerVault,
  type PickerWorkRootIds,
} from "@/lib/brand-site-asset-picker";
import {
  BrandSiteApiError,
  referenceBrandSiteMaterial,
} from "@/lib/brand-site-api";
import { kbMaterialsPath } from "@/lib/kb-routes";
import {
  createMaterialsApi,
  type MaterialsFolder,
  type MaterialsItem,
} from "@/lib/materials-api";
import type { MaterialsFolderTreeNode } from "@/lib/materials-folder-tree";
import { previewKind, previewKindLabel } from "@/lib/materials-preview-kind";
import {
  buildExternalVideoEmbedHtml,
  externalVideoEmbedUrl,
  isExternalVideoItem,
} from "@/lib/materials-external-video";
import { useMaterialsUploadQueue } from "@/lib/materials-upload-queue";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  brandId: number;
  brandSiteId: number;
  /** 当前编辑页 slug，用于登记素材引用位置。 */
  pageSlug?: string | null;
  /** 保留入参兼容；面板预览直接读取 Materials 内容。 */
  publicUrl?: string | null;
  /** copy：多选复制素材编号与文件名；pick：单选图片并回填代发路径。 */
  mode?: "copy" | "pick";
  onPick?: (proxyPath: string) => void;
  /** 非 pick 模式：将 HTML 片段插入当前编辑页（外链视频等）。 */
  onInsertHtml?: (html: string) => Promise<void>;
};

type VaultData = {
  folders: MaterialsFolder[];
  workRootId: number | null;
};

type VaultDataMap = Record<PickerVault, VaultData>;

const EMPTY_VAULT_DATA: VaultDataMap = {
  enterprise: { folders: [], workRootId: null },
  brand: { folders: [], workRootId: null },
  site: { folders: [], workRootId: null },
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatCreatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function isImage(item: MaterialsItem): boolean {
  return previewKind(item) === "image";
}

function copyRows(rows: PickerListRow[]): string {
  return rows.map(({ item }) => `${item.slug} ${item.name}`).join("\n");
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof Error && caught.message ? caught.message : fallback;
}

/** 三仓目录式素材选择器：左树、中列表、右预览。 */
export function BrandSiteAssetPicker({
  open,
  onOpenChange,
  accountId,
  brandId,
  brandSiteId,
  pageSlug = null,
  mode = "copy",
  onPick,
  onInsertHtml,
}: Props) {
  const pickMode = mode === "pick";
  const materialsApi = useMemo(() => createMaterialsApi(accountId), [accountId]);
  const { enqueueUploads, subscribeItemDone } = useMaterialsUploadQueue();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const listRequestRef = useRef(0);

  const [vaultData, setVaultData] = useState<VaultDataMap>(EMPTY_VAULT_DATA);
  const [nav, setNav] = useState<PickerNav>({ vault: "site", folderId: null });
  const [items, setItems] = useState<MaterialsItem[]>([]);
  const [query, setQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState<Map<string, PickerListRow>>(
    () => new Map(),
  );
  const [focusRow, setFocusRow] = useState<PickerListRow | null>(null);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersReady, setFoldersReady] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewEmbedUrl, setPreviewEmbedUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoInput, setVideoInput] = useState("");
  const [videoSaving, setVideoSaving] = useState(false);
  const [inserting, setInserting] = useState(false);

  const forest = useMemo(
    () =>
      buildPickerVaultForest({
        enterprise: vaultData.enterprise,
        brand: vaultData.brand,
        site: vaultData.site,
      }),
    [vaultData],
  );

  const visibleItems = useMemo(() => {
    const filtered = filterPickerItems(items, query);
    return pickMode ? filtered.filter(isImage) : filtered;
  }, [items, pickMode, query]);
  const visibleRows = useMemo(
    () => buildPickerListRows(nav.vault, visibleItems),
    [nav.vault, visibleItems],
  );

  const currentVault = vaultData[nav.vault];
  const workRootIds: PickerWorkRootIds = {
    enterprise: vaultData.enterprise.workRootId,
    brand: vaultData.brand.workRootId,
    site: vaultData.site.workRootId,
  };
  const currentStorageFolderId = pickerUploadFolderId(nav, workRootIds);

  const loadFolders = useCallback(async () => {
    setFoldersLoading(true);
    setFoldersReady(false);
    setError(null);
    try {
      // 默认落在「本站素材」：先出站点树，再补品牌/账号，避免三仓齐等 + 全量企业树拖慢首屏
      const site = await materialsApi.listFolders({
        ...pickerListContext("site", brandId, brandSiteId),
        lean: true,
      });
      setVaultData((previous) => ({
        ...previous,
        site: { folders: site.folders ?? [], workRootId: site.work_root_id },
      }));
      setFoldersReady(true);
      setFoldersLoading(false);

      const [enterprise, brand] = await Promise.all([
        materialsApi.listFolders({
          ...pickerListContext("enterprise", brandId, brandSiteId),
          lean: true,
        }),
        materialsApi.listFolders({
          ...pickerListContext("brand", brandId, brandSiteId),
          lean: true,
        }),
      ]);
      setVaultData({
        enterprise: {
          folders: enterprise.folders ?? [],
          workRootId: enterprise.work_root_id,
        },
        brand: { folders: brand.folders ?? [], workRootId: brand.work_root_id },
        site: { folders: site.folders ?? [], workRootId: site.work_root_id },
      });
    } catch (caught) {
      setError(errorMessage(caught, "目录加载失败"));
      setVaultData(EMPTY_VAULT_DATA);
      setFoldersReady(false);
      setFoldersLoading(false);
    }
  }, [brandId, brandSiteId, materialsApi]);

  useEffect(() => {
    if (!open) return;
    setNav({ vault: "site", folderId: null });
    setQuery("");
    setSelectedRows(new Map());
    setFocusRow(null);
    setItems([]);
    void loadFolders();
  }, [open, loadFolders]);

  useEffect(() => {
    if (!open || foldersLoading || !foldersReady) return;
    const requestId = ++listRequestRef.current;
    const itemsQuery = pickerItemsQuery(nav, workRootIds, query, brandId, brandSiteId);

    setItemsLoading(true);
    setError(null);
    void materialsApi
      .listItems(itemsQuery)
      .then((payload) => {
        if (requestId !== listRequestRef.current) return;
        setItems(payload.items ?? []);
      })
      .catch((caught) => {
        if (requestId !== listRequestRef.current) return;
        setItems([]);
        setError(errorMessage(caught, "素材加载失败"));
      })
      .finally(() => {
        if (requestId === listRequestRef.current) setItemsLoading(false);
      });
  }, [
    brandId,
    brandSiteId,
    foldersReady,
    foldersLoading,
    materialsApi,
    nav,
    open,
    query,
    reloadToken,
    vaultData.brand.workRootId,
    vaultData.enterprise.workRootId,
    vaultData.site.workRootId,
  ]);

  useEffect(() => {
    if (!open) return;
    return subscribeItemDone((doneAccountId, item) => {
      if (String(doneAccountId) !== String(accountId)) return;
      setReloadToken((value) => value + 1);
      if (item.folder_id !== currentStorageFolderId) return;
      const [row] = buildPickerListRows(nav.vault, [item]);
      if (!row) return;
      setSelectedRows((previous) => {
        const next = new Map(previous);
        if (pickMode) next.clear();
        next.set(row.key, row);
        return next;
      });
      setFocusRow(row);
    });
  }, [
    accountId,
    currentStorageFolderId,
    nav.vault,
    open,
    pickMode,
    subscribeItemDone,
  ]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setPreviewUrl(null);
    if (!open || !focusRow) {
      setPreviewLoading(false);
      setPreviewEmbedUrl(null);
      return;
    }

    if (isExternalVideoItem(focusRow.item)) {
      setPreviewEmbedUrl(externalVideoEmbedUrl(focusRow.item));
      setPreviewLoading(false);
      return;
    }

    setPreviewEmbedUrl(null);
    const kind = previewKind(focusRow.item);
    if (kind !== "image" && kind !== "video" && kind !== "audio") {
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    void materialsApi
      .fetchItemContent(focusRow.item.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [focusRow, materialsApi, open]);

  function navigate(nextNav: PickerNav) {
    setNav(nextNav);
    setQuery("");
    setItems([]);
    setFocusRow(null);
  }

  function toggleRow(row: PickerListRow, checked: boolean) {
    setSelectedRows((previous) => {
      const next = new Map(previous);
      if (pickMode) next.clear();
      if (checked) next.set(row.key, row);
      else next.delete(row.key);
      return next;
    });
    setFocusRow(row);
  }

  async function referenceRow(row: PickerListRow): Promise<string> {
    const payload = await referenceBrandSiteMaterial(
      accountId,
      brandSiteId,
      buildPickerReferencePayload(row.item, pageSlug),
    );
    const path = payload.asset?.proxy_path?.trim();
    if (!path?.startsWith("/_c456/a/")) throw new Error("登记素材失败");
    return path;
  }

  async function insertExternalVideoItem(item: MaterialsItem) {
    if (!onInsertHtml) return;
    const embedUrl = externalVideoEmbedUrl(item);
    if (!embedUrl) {
      toast.error("地址不对");
      return;
    }
    setInserting(true);
    try {
      const html = buildExternalVideoEmbedHtml(embedUrl, item.slug);
      await onInsertHtml(html);
      onOpenChange(false);
      toast.success("已插入视频");
    } catch (caught) {
      toast.error(
        caught instanceof BrandSiteApiError
          ? caught.message
          : errorMessage(caught, "插入失败，请重试"),
      );
    } finally {
      setInserting(false);
    }
  }

  async function saveExternalVideoAndInsert() {
    const input = videoInput.trim();
    if (!input) {
      toast.error("地址不对");
      return;
    }
    setVideoSaving(true);
    try {
      const { item } = await materialsApi.createExternalVideo({
        external_video_input: input,
        folder_id: currentStorageFolderId,
        brand_id: nav.vault === "enterprise" ? undefined : brandId,
      });
      setVideoDialogOpen(false);
      setVideoInput("");
      setReloadToken((value) => value + 1);
      if (onInsertHtml && item.external_embed_url) {
        await insertExternalVideoItem(item);
      } else {
        toast.success("已添加视频");
      }
    } catch (caught) {
      toast.error(
        caught instanceof BrandSiteApiError
          ? caught.message
          : errorMessage(caught, "添加失败"),
      );
    } finally {
      setVideoSaving(false);
    }
  }

  async function confirmPick(row?: PickerListRow) {
    const selected = row ?? selectedRows.values().next().value;
    if (!selected || !onPick) {
      toast.message("请先选一张图");
      return;
    }
    setPicking(true);
    try {
      const proxyPath = await referenceRow(selected);
      onPick(proxyPath);
      onOpenChange(false);
      toast.success("已选用封面图");
    } catch (caught) {
      toast.error(
        caught instanceof BrandSiteApiError
          ? caught.message
          : errorMessage(caught, "选用失败，请重试"),
      );
    } finally {
      setPicking(false);
    }
  }

  async function copySelected() {
    const rows = Array.from(selectedRows.values());
    if (rows.length === 0) {
      toast.message("请先勾选素材");
      return;
    }
    try {
      await navigator.clipboard.writeText(copyRows(rows));
      toast.success(rows.length === 1 ? "已复制编号和名称" : `已复制 ${rows.length} 项`);
    } catch {
      toast.error("复制失败");
    }
  }

  function uploadFiles(fileList: FileList | null) {
    if (!fileList?.length) return;
    const files = Array.from(fileList);
    const folderId = currentStorageFolderId;
    enqueueUploads({
      accountId,
      files,
      folderId,
      brandId: nav.vault === "enterprise" ? null : brandId,
      materialsPath: kbMaterialsPath({
        accountId,
        brandId: nav.vault === "enterprise" ? undefined : String(brandId),
        brandSiteId: nav.vault === "site" ? String(brandSiteId) : undefined,
      }),
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(88vh,44rem)] w-full max-w-[min(96vw,72rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(96vw,72rem)]">
        <DialogHeader className="shrink-0 space-y-1 border-b border-border px-4 py-3">
          <DialogTitle>{pickMode ? "选择图片" : "插入素材"}</DialogTitle>
          <DialogDescription>
            {pickMode
              ? "从账号、品牌或本站素材中选一张图片。"
              : onInsertHtml
                ? "查找素材、添加外链视频，或复制编号给 AI。"
                : "按目录查找素材，勾选后复制素材编号和文件名。"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`搜索${PICKER_VAULT_LABEL[nav.vault]}的编号、文件名或备注`}
              className="h-8 pl-8"
              aria-label="搜索素材"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={(event) => uploadFiles(event.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            disabled={foldersLoading || currentVault.workRootId == null && nav.vault !== "enterprise"}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="size-3.5" />
            上传到当前目录
          </Button>
          {!pickMode ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              disabled={foldersLoading}
              onClick={() => {
                setVideoInput("");
                setVideoDialogOpen(true);
              }}
            >
              <Video className="size-3.5" />
              添加视频
            </Button>
          ) : null}
        </div>

        {error ? (
          <p className="shrink-0 border-b border-destructive/20 bg-destructive/5 px-4 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="grid min-h-0 flex-1 grid-cols-[14rem_minmax(18rem,1fr)_minmax(14rem,18rem)]">
          <aside className="min-h-0 overflow-hidden border-r border-border bg-muted/10">
            <PickerVaultTree
              forest={forest}
              nav={nav}
              loading={foldersLoading}
              onNavigate={navigate}
            />
          </aside>

          <main className="min-h-0 overflow-auto border-r border-border">
            {itemsLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                加载中…
              </div>
            ) : visibleRows.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-muted-foreground">
                {query.trim()
                  ? "当前素材仓没有匹配结果。"
                  : "当前目录还没有文件，可上传或切换目录。"}
              </p>
            ) : (
              <ul className="divide-y divide-border/70">
                {visibleRows.map((row) => {
                  const { item } = row;
                  const selected = selectedRows.has(row.key);
                  const focused = focusRow?.key === row.key;
                  return (
                    <li key={row.key}>
                      <div
                        className={cn(
                          "flex items-stretch gap-1 px-2 py-2 hover:bg-muted/40",
                          focused && "bg-primary/5",
                        )}
                      >
                        <div className="flex w-9 shrink-0 items-center justify-center">
                          <Checkbox
                            checked={selected}
                            aria-label={`选择 ${item.name}`}
                            className="after:hidden"
                            onCheckedChange={(value) => toggleRow(row, value === true)}
                          />
                        </div>
                        <button
                          type="button"
                          className="min-w-0 flex-1 px-1 text-left"
                          onClick={() => {
                            setFocusRow(row);
                            if (pickMode) toggleRow(row, true);
                          }}
                          onDoubleClick={() => {
                            if (pickMode) void confirmPick(row);
                          }}
                        >
                          <span className="block truncate text-sm font-medium">{item.name}</span>
                          <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span className="font-mono">{item.slug}</span>
                            <span>·</span>
                            <span>{previewKindLabel(previewKind(item))}</span>
                            <span>·</span>
                            <span>{formatCreatedAt(item.created_at)}</span>
                          </span>
                          {item.notes ? (
                            <span className="mt-1 block truncate text-[11px] text-muted-foreground">
                              {item.notes}
                            </span>
                          ) : null}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </main>

          <aside className="flex min-h-0 flex-col overflow-hidden bg-muted/20 p-3">
            {focusRow ? (
              <PreviewPane
                row={focusRow}
                previewUrl={previewUrl}
                previewEmbedUrl={previewEmbedUrl}
                previewLoading={previewLoading}
              />
            ) : (
              <p className="m-auto text-center text-xs text-muted-foreground">
                点文件名查看预览
              </p>
            )}
          </aside>
        </div>

        <DialogFooter className="shrink-0 flex-col gap-2 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            已选 <strong className="text-foreground">{selectedRows.size}</strong> 项
            {pickMode ? "（封面只需一张）" : null}
          </p>
          {pickMode ? (
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1"
              disabled={selectedRows.size === 0 || picking}
              onClick={() => void confirmPick()}
            >
              {picking ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <ImagePlus className="size-3.5" />
              )}
              选用此图
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {onInsertHtml &&
              Array.from(selectedRows.values()).some((row) => isExternalVideoItem(row.item)) ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1"
                  disabled={inserting}
                  onClick={() => {
                    const row = Array.from(selectedRows.values()).find((r) =>
                      isExternalVideoItem(r.item),
                    );
                    if (row) void insertExternalVideoItem(row.item);
                  }}
                >
                  {inserting ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Video className="size-3.5" />
                  )}
                  插入视频
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1"
                disabled={selectedRows.size === 0}
                onClick={() => void copySelected()}
              >
                <Copy className="size-3.5" />
                复制编号和名称
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>

      <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>添加视频</DialogTitle>
            <DialogDescription>
              粘贴插入代码或视频链接。{onInsertHtml ? "添加成功后将自动插入当前页面。" : ""}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            className="min-h-28 font-mono text-sm"
            value={videoInput}
            onChange={(event) => setVideoInput(event.target.value)}
            placeholder="腾讯视频 / B 站插入代码或链接"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setVideoDialogOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              disabled={videoSaving}
              onClick={() => void saveExternalVideoAndInsert()}
            >
              {videoSaving ? <Loader2 className="size-3.5 animate-spin" /> : null}
              添加{onInsertHtml ? "并插入" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function PickerVaultTree({
  forest,
  nav,
  loading,
  onNavigate,
}: {
  forest: ReturnType<typeof buildPickerVaultForest>;
  nav: PickerNav;
  loading: boolean;
  onNavigate: (nav: PickerNav) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(["vault:enterprise", "vault:brand", "vault:site"]),
  );

  function toggle(key: string) {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <nav className="h-full overflow-y-auto px-2 py-3 text-sm" aria-label="素材目录">
      {loading ? (
        <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          加载目录…
        </div>
      ) : (
        <ul className="flex flex-col gap-1" role="tree">
          {forest.map((vault) => {
            const key = `vault:${vault.vault}`;
            const isExpanded = expanded.has(key);
            const selected = nav.vault === vault.vault && nav.folderId == null;
            const VaultIcon =
              vault.vault === "enterprise"
                ? Building2
                : vault.vault === "brand"
                  ? Folder
                  : Globe;
            return (
              <li key={vault.vault} role="treeitem" aria-expanded={isExpanded}>
                <div
                  className={cn(
                    "flex items-stretch rounded-md hover:bg-muted",
                    selected && "bg-muted font-medium",
                  )}
                >
                  <button
                    type="button"
                    className="flex w-6 shrink-0 items-center justify-center text-muted-foreground"
                    aria-label={isExpanded ? `折叠${vault.label}` : `展开${vault.label}`}
                    onClick={() => toggle(key)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="size-3.5" />
                    ) : (
                      <ChevronRight className="size-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pr-2 text-left"
                    onClick={() => onNavigate({ vault: vault.vault, folderId: null })}
                  >
                    <VaultIcon className="size-3.5 shrink-0 text-primary" />
                    <span className="truncate">{vault.label}</span>
                  </button>
                </div>
                {isExpanded && vault.children.length > 0 ? (
                  <ul role="group">
                    {vault.children.map((node) => (
                      <PickerFolderNode
                        key={node.folder.id}
                        vault={vault.vault}
                        node={node}
                        nav={nav}
                        depth={0}
                        expanded={expanded}
                        onToggle={toggle}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}

function PickerFolderNode({
  vault,
  node,
  nav,
  depth,
  expanded,
  onToggle,
  onNavigate,
}: {
  vault: PickerVault;
  node: MaterialsFolderTreeNode;
  nav: PickerNav;
  depth: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onNavigate: (nav: PickerNav) => void;
}) {
  const key = `${vault}:folder:${node.folder.id}`;
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(key);
  const selected = nav.vault === vault && nav.folderId === node.folder.id;

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
      <div
        className={cn(
          "flex items-stretch rounded-md hover:bg-muted",
          selected && "bg-muted font-medium",
        )}
        style={{ paddingLeft: `${depth * 12 + 12}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="flex w-6 shrink-0 items-center justify-center text-muted-foreground"
            aria-label={isExpanded ? `折叠${node.folder.name}` : `展开${node.folder.name}`}
            onClick={() => onToggle(key)}
          >
            {isExpanded ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
          </button>
        ) : (
          <span className="w-6 shrink-0" aria-hidden />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 pr-2 text-left"
          onClick={() => onNavigate({ vault, folderId: node.folder.id })}
        >
          {selected ? (
            <FolderOpen className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Folder className="size-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{node.folder.name}</span>
        </button>
      </div>
      {isExpanded ? (
        <ul role="group">
          {node.children.map((child) => (
            <PickerFolderNode
              key={child.folder.id}
              vault={vault}
              node={child}
              nav={nav}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function PreviewPane({
  row,
  previewUrl,
  previewEmbedUrl,
  previewLoading,
}: {
  row: PickerListRow;
  previewUrl: string | null;
  previewEmbedUrl: string | null;
  previewLoading: boolean;
}) {
  const kind = previewKind(row.item);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="shrink-0">
        <p className="truncate text-sm font-medium">{row.item.name}</p>
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {row.item.slug}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {PICKER_VAULT_LABEL[row.vault]} · {previewKindLabel(kind)} ·{" "}
          {formatBytes(row.item.byte_size)} · {formatCreatedAt(row.item.created_at)}
        </p>
      </div>
      <PreviewMedia
        kind={kind}
        url={previewUrl}
        embedUrl={previewEmbedUrl}
        loading={previewLoading}
        title={row.item.name}
      />
      {row.item.notes ? (
        <p className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
          {row.item.notes}
        </p>
      ) : null}
    </div>
  );
}

function PreviewMedia({
  kind,
  url,
  embedUrl,
  loading,
  title,
}: {
  kind: string;
  url: string | null;
  embedUrl?: string | null;
  loading: boolean;
  title: string;
}) {
  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-border bg-background text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }
  if (embedUrl) {
    return (
      <div className="aspect-video overflow-hidden rounded-md border border-border bg-black">
        <iframe
          title={title}
          src={embedUrl}
          className="h-full w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          allowFullScreen
        />
      </div>
    );
  }
  if (kind === "image" && url) {
    return (
      <div className="overflow-hidden rounded-md border border-border bg-background">
        <img src={url} alt={title} className="max-h-52 w-full object-contain" />
      </div>
    );
  }
  if (kind === "video" && url) {
    return (
      <video
        src={url}
        controls
        className="max-h-52 w-full rounded-md border border-border bg-black"
      />
    );
  }
  if (kind === "audio" && url) return <audio src={url} controls className="w-full" />;
  return (
    <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-border bg-background px-3 text-center text-xs text-muted-foreground">
      此类型暂无缩略图
    </div>
  );
}

type BrandSiteAssetPickerTriggerProps = Omit<Props, "open" | "onOpenChange"> & {
  className?: string;
};

export function BrandSiteAssetPickerTrigger(props: BrandSiteAssetPickerTriggerProps) {
  const [open, setOpen] = useState(false);
  const { className, ...pickerProps } = props;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className}
        onClick={() => setOpen(true)}
      >
        <ImagePlus className="size-3.5" />
        插入素材
      </Button>
      <BrandSiteAssetPicker open={open} onOpenChange={setOpen} {...pickerProps} />
    </>
  );
}
